// Club page data: mirrors screens/ClubDetailsScreen.js loadClubDetails (:314-420).
// getClubById -> club + members + events payload; getUserApplications and
// getUserMembership in parallel. Epoch-guarded so a stale club never paints.
import { useCallback, useEffect, useRef, useState } from "react";
import { ClubService } from "../../../../services/clubService";
import { isClubLeaderRole } from "../../../../utils/notificationTargeting";
import { msg } from "./clubUtils";

const withUserId = (m) => ({ ...m, user_id: m.user_id || m.user?.id });

export default function useClubDetail(clubId, user, seed) {
  const [club, setClub] = useState(seed && seed.id === clubId ? seed : null);
  const [members, setMembers] = useState([]);
  const [memberCount, setMemberCount] = useState(seed?.member_count ?? 0);
  const [payloadEvents, setPayloadEvents] = useState(null); // null = events query failed / not loaded
  const [loading, setLoading] = useState(!(seed && seed.id === clubId));
  const [error, setError] = useState(null);
  const [mem, setMem] = useState({ resolved: false, isMember: false, isAdmin: false, isStrict: false, hasApplied: false });
  const epoch = useRef(0);
  const userEpoch = useRef(0);
  const uid = user?.uid;

  const loadUser = useCallback(async (myEpoch) => {
    const myUser = ++userEpoch.current;
    if (!uid) {
      setMem({ resolved: true, isMember: false, isAdmin: false, isStrict: false, hasApplied: false });
      return;
    }
    try {
      const [apps, membership] = await Promise.all([
        ClubService.getUserApplications(uid),
        ClubService.getUserMembership(clubId, uid),
      ]);
      if (myEpoch !== epoch.current || myUser !== userEpoch.current) return;
      setMem((prev) => {
        const next = { ...prev };
        if (!apps.error && apps.data) next.hasApplied = apps.data.some((a) => a.clubId === clubId && a.status === "pending");
        if (!membership.error && membership.data) {
          const rows = membership.data;
          next.isMember = rows.length > 0;
          next.isAdmin = rows.length > 0 && isClubLeaderRole(rows[0]?.role);
          next.isStrict = rows.length > 0 && rows[0]?.role === "admin";
          next.resolved = true;
        } else {
          next.isMember = false; next.isAdmin = false; next.isStrict = false; next.resolved = false;
        }
        return next;
      });
    } catch {
      /* membership lookup failure never blocks the club view */
    }
  }, [clubId, uid]);

  const load = useCallback(async () => {
    const myEpoch = ++epoch.current;
    const { data, error: e } = await ClubService.getClubById(clubId);
    if (myEpoch !== epoch.current) return;
    if (e || !data) {
      setError(msg(e, "Failed to load club details"));
      setLoading(false);
      loadUser(myEpoch); // membership/application flags don't depend on the detail fetch
      return;
    }
    setError(null);
    const initialMembers = (data.members || []).map(withUserId);
    setPayloadEvents(data.events ?? null);
    setClub(data);
    setMembers(initialMembers);
    setMemberCount(initialMembers.length);
    setLoading(false);
    if (data.member_count !== initialMembers.length) ClubService.syncMemberCount(clubId).catch(() => {});
    loadUser(myEpoch);
  }, [clubId, loadUser]);

  useEffect(() => {
    const seeded = seed && seed.id === clubId;
    setClub(seeded ? seed : null);
    setLoading(!seeded);
    setError(null);
    setMem({ resolved: false, isMember: false, isAdmin: false, isStrict: false, hasApplied: false });
    load();
    return () => { epoch.current += 1; };
    // seed is only a first-paint hint; reload when the route/user changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, uid]);

  /** Re-read just the viewer's membership/application flags. */
  const refreshUser = useCallback(() => loadUser(epoch.current), [loadUser]);

  return { club, members, setMembers, memberCount, setMemberCount, payloadEvents, loading, error, mem, setMem, reload: load, refreshUser };
}
